import TextTooltip from "components/shared/TextTooltip";
import lightFormat from "date-fns/lightFormat";
import type { IAmericanCompanyIssuerFinancialsFieldsFragment } from "lib/fragments/__generated__/AmericanCompanyIssuerTopLevel.generated";
import { palette, Popover } from "lib/miolo";
import parseDateYmd from "lib/utils/parseDateYmd";
import React, { useState } from "react";

interface IFiscalYearTableCellProps {
  row:
    | IAmericanCompanyIssuerFinancialsFieldsFragment["yearly"][0]
    | IAmericanCompanyIssuerFinancialsFieldsFragment["quarterly"][0];
  onFilterByQuarter: (reportDate: Date) => void;
}

export default function FiscalYearTableCell({ row, onFilterByQuarter }: IFiscalYearTableCellProps) {
  const reportDate = parseDateYmd(row.reportDate);
  const [isOpen, setIsOpen] = useState(false);
  return (
    <td
      onMouseEnter={() => {
        if (row.linkHref) {
          setIsOpen(true);
        }
      }}
      onMouseLeave={() => setIsOpen(false)}
      css={{
        fontFamily: "novel-mono-pro-condensed",
        backgroundColor: palette.blueBayoux,
        fontWeight: 600,
        color: palette.white,
        lineHeight: "20px",
        borderTop: `solid 1px ${palette.lynch}`,
        borderBottom: `solid 1px ${palette.lynch}`,
        borderRight: `solid 1px ${palette.lynch}`,
      }}
    >
      {row.statementType === "TTM" ? (
        <TextTooltip
          id={`tooltips/th/trailingTwelveMonths-${row.fiscalYear}`}
          placement="top"
          content="Trailing twelve months"
          style={{
            maxWidth: 260,
            textAlign: "center",
          }}
        >
          {({ accessibility, bind, ref }) => (
            <span {...accessibility} {...bind} ref={ref}>
              {row.fiscalYear}
            </span>
          )}
        </TextTooltip>
      ) : (
        <Popover
          trapFocus={true}
          id={`FiscalYearTableCellButton/${row.reportDate}`}
          isOpen={isOpen}
          arrowColor={palette.white}
          renderWithPortal={false}
          backgroundColor={palette.white}
          containerStyle={{
            zIndex: 9999,
          }}
          render={() => {
            if (!row.linkHref) {
              return <div />;
            }
            return (
              <div className="p-1">
                <div className="relative flex flex-col space-y-1 overflow-hidden rounded-md bg-violet-50 p-2 font-aeonik tracking-wide">
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-3 text-sm font-bold text-violet-500 hover:underline"
                    href={row.linkHref}
                  >
                    <span>Ver na SEC</span>
                    <ExternalLinkIcon className="h-4 w-4" />
                  </a>
                </div>
              </div>
            );
          }}
        >
          {({ accessibility, ref }) => (
            <div {...accessibility} ref={ref}>
              <button
                type="button"
                aria-label={`Exibir apenas ${lightFormat(reportDate, "dd/MM")}`}
                onClick={() => {
                  if (row.statementType === "QUARTERLY") {
                    onFilterByQuarter(reportDate);
                  }
                }}
                css={{
                  margin: 0,
                  padding: 0,
                  color: "inherit",
                  fontWeight: "inherit",
                  backgroundColor: "transparent",
                  border: "none",
                  outline: "none",
                  cursor: "pointer",
                  pointerEvents: row.statementType === "QUARTERLY" ? "all" : "none",
                }}
              >
                {row.fiscalYear}
              </button>
            </div>
          )}
        </Popover>
      )}
    </td>
  );
}

function ExternalLinkIcon(props: React.SVGProps<any>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
      />
    </svg>
  );
}
